/**
 * KillSheet - Displays rejected arguments from the debate
 * 
 * Shows why certain arguments were rejected during the multi-agent debate,
 * with structured rejection reasons and counter-evidence.
 * 
 * Designed for Marcus (Financial Analyst) persona who wants to see
 * "Why the Bear Case Failed" with explicit reasoning.
 */

import { useState } from 'react';
import { 
  XCircle, 
  ChevronDown, 
  ChevronUp,
  Clock,
  FileWarning,
  Users,
  Minimize2,
  TrendingUp,
  HelpCircle,
  AlertTriangle,
  ExternalLink
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { RejectedArgument, RejectionReason } from '@/types/researchModes';
import { REJECTION_REASON_CONFIG } from '@/types/researchModes';

interface KillSheetProps {
  rejectedArguments: RejectedArgument[];
  className?: string;
  title?: string;
  collapsed?: boolean;
  hideHeader?: boolean;  // When true, renders only the content without the collapsible header
}

// Icon mapping for rejection reasons
const REJECTION_ICONS: Record<RejectionReason, React.ReactNode> = {
  REJECTED_OUTDATED: <Clock className="w-4 h-4" />,
  REJECTED_SOURCE_TIER: <FileWarning className="w-4 h-4" />,
  REJECTED_CONSENSUS: <Users className="w-4 h-4" />,
  REJECTED_MAGNITUDE: <Minimize2 className="w-4 h-4" />,
  REJECTED_PRICED_IN: <TrendingUp className="w-4 h-4" />,
  REJECTED_SPECULATIVE: <HelpCircle className="w-4 h-4" />,
  REJECTED_CONTRADICTED: <XCircle className="w-4 h-4" />,
};

function RejectedArgumentCard({ argument }: { argument: RejectedArgument }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const reasonConfig = REJECTION_REASON_CONFIG[argument.rejectionReason];

  return (
    <div className="bg-surface/50 border border-border rounded-lg overflow-hidden">
      {/* Header - Always visible */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-surface-light/30 transition-colors"
      >
        {/* Rejection icon */}
        <div className="mt-0.5 p-1.5 rounded-md bg-red-500/10 text-red-400">
          <XCircle className="w-4 h-4" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-text-primary">
              Rejected: {argument.argumentSummary}
            </span>
          </div>
          
          <div className="flex items-center gap-3 mt-1.5 text-xs">
            <span className="text-text-muted">
              Argued by: <span className="text-text-secondary">{argument.arguedBy}</span>
              {argument.agentModel && (
                <span className="text-text-muted"> ({argument.agentModel})</span>
              )}
            </span>
            
            {/* Rejection reason badge */}
            <span className={cn(
              'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs',
              'bg-surface-light border border-border',
              reasonConfig.color
            )}>
              {REJECTION_ICONS[argument.rejectionReason]}
              {reasonConfig.shortLabel}
            </span>
          </div>
        </div>

        {/* Expand/collapse */}
        <div className="text-text-muted">
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {/* Expanded details */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 border-t border-border/50">
          <div className="pl-10 space-y-3">
            {/* Rejection detail */}
            <div>
              <p className="text-xs text-text-muted uppercase tracking-wider mb-1">
                Why it was rejected
              </p>
              <p className="text-sm text-text-primary leading-relaxed">
                {argument.rejectionDetail}
              </p>
            </div>

            {/* Counter evidence */}
            {argument.counterEvidence && (
              <div>
                <p className="text-xs text-text-muted uppercase tracking-wider mb-1">
                  Counter Evidence
                </p>
                <p className="text-sm text-text-primary leading-relaxed">
                  {argument.counterEvidence}
                </p>
              </div>
            )}

            {/* Sources cited */}
            {argument.sourcesCited && argument.sourcesCited.length > 0 && (
              <div>
                <p className="text-xs text-text-muted uppercase tracking-wider mb-1">
                  Sources
                </p>
                <div className="flex flex-wrap gap-2">
                  {argument.sourcesCited.map((source, idx) => (
                    <a
                      key={idx}
                      href={source}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {new URL(source).hostname}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function KillSheet({
  rejectedArguments,
  className,
  title = "Why the Bear Case Failed",
  collapsed: initialCollapsed = false,
  hideHeader = false,
}: KillSheetProps) {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);

  if (rejectedArguments.length === 0) {
    return null;
  }

  // Headerless mode - just render the content
  if (hideHeader) {
    return (
      <div className={cn('px-4 pb-4 space-y-3', className)}>
        {rejectedArguments.map((arg) => (
          <RejectedArgumentCard key={arg.id} argument={arg} />
        ))}
      </div>
    );
  }

  return (
    <div className={cn('rounded-xl border border-red-500/20 bg-red-500/5 overflow-hidden', className)}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between p-4 hover:bg-red-500/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-red-500/10">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-red-400">{title}</h3>
            <p className="text-xs text-text-muted">
              {rejectedArguments.length} argument{rejectedArguments.length !== 1 ? 's' : ''} rejected
            </p>
          </div>
        </div>
        <div className="text-text-muted">
          {isCollapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
        </div>
      </button>

      {/* Content */}
      {!isCollapsed && (
        <div className="px-4 pb-4 space-y-3">
          {rejectedArguments.map((arg) => (
            <RejectedArgumentCard key={arg.id} argument={arg} />
          ))}
        </div>
      )}
    </div>
  );
}

export default KillSheet;

